import { useContext, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { isEmpty, omit } from 'lodash/fp';
import { RelationshipInformationVisual } from './relationshipInformation.visual';
import {
  type PhysicalAddress,
  SubStepTypes,
  TrustedContactRelationship,
  type User,
} from '../../../../../interfaces';
import { usePermissions, UserContext } from '../../../../../providers/userContextProvider';
import { formatPhoneNumber } from '../../../../../util';
import { CustomField, FETCH_CUSTOM_FIELDS, modifyUpdatingCustomFields } from '../../../../../interfaces/customField';
import { getEnabledCustomFields } from '../utils';

const FETCH_USER = (permissions: string[], derivedCustomFields?: string[]) => gql`
  query fetchUserRelationshipInfo($userId: ObjectID!) {
    fetchUser(userId: $userId) {
      user {
        id
        maritalStatus
        spouseFirstName
        spouseMiddleName
        spouseLastName
        spouseEmail
        spouseTaxIdType
        spouseTaxId
        ${permissions.includes('read:client_taxid_disclosed') ? 'spouseTaxIdDisclosed' : ''}
        spouseDateOfBirth
        spouseEmploymentStatus
        spouseEmployerName
        spouseEmployerCompanyAddress { city streetName postal unitNumber houseNumber neighborhood country jurisdiction }
        spouseEmployerCompanyPhone
        spouseEmploymentType
        spouseCompanyType
        spouseJobTitle
        spouseAreaOfStudy
        spouseAnnualIncomeCents
        spouseAnnualIncomeStr
        numberOfDependents
        trustedContactPerson {
          name
          relation
          physicalAddress { city streetName postal unitNumber houseNumber neighborhood country jurisdiction }
          email
          phone
        }
        type
        countryOfTaxResidence
        physicalAddress { country }
        ${derivedCustomFields ? `customFields(keys: ${JSON.stringify(derivedCustomFields)}) {
          key
          value
          customField { id translatedName { en fr } translatedDescription{ en fr} type format}
          selectedOptions { value displayText { en fr } }
        }` : ''}
      }
    }
  }
`;

const UPDATE_USER = gql`
  mutation updateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      user { id }
    }
  }
`;

const INITIAL_USER_DATA: Partial<User> = {
  maritalStatus: undefined,
  spouseFirstName: '',
  spouseMiddleName: '',
  spouseLastName: '',
  spouseEmail: '',
  spouseTaxId: '',
  spouseTaxIdType: undefined,
  spouseEmploymentType: '',
  spouseCompanyType: '',
  spouseJobTitle: '',
  spouseAreaOfStudy: '',
  spouseDateOfBirth: '',
  spouseEmploymentStatus: undefined,
  spouseEmployerName: '',
  spouseEmployerCompanyAddress: undefined,
  spouseEmployerCompanyPhone: '',
  spouseAnnualIncomeCents: undefined,
  spouseAnnualIncomeStr: undefined,
  numberOfDependents: 0,
  customFields: [],
  trustedContactPerson: {
    name: '',
    physicalAddress: {
      city: '',
      jurisdiction: '',
      streetName: '',
      postal: '',
      unitNumber: '',
      houseNumber: '',
      neighborhood: '',
      country: undefined,
    },
    email: '',
    phone: '',
    relation: '' as TrustedContactRelationship,
  },
};

export const RelationshipInformation = ({
  options, userId, onNext, stepLoading, workflowCompletion, grid = false, updateMode = false,
}: {
  options: any, userId: string, onNext?: () => void, stepLoading: boolean, workflowCompletion?: any, grid?: boolean, updateMode?: boolean,
}) => {
  const { permissions } = usePermissions();
  const [derivedCustomFields, setDerivedCustomFields] = useState<CustomField[]>();
  const [derivedCustomFieldsKeys, setDerivedCustomFieldsKeys] = useState<string[]>();
  const enabledCustomFields = getEnabledCustomFields(options);
  const { activeOrganization } = useContext(UserContext);

  const [userData, setUserData] = useState(INITIAL_USER_DATA);

  const customFieldsPermitted = permissions.includes('read:custom_fields') && enabledCustomFields.length !== 0;

  // loading 1 - custom fields
  const { loading: customFieldsLoading } = useQuery(FETCH_CUSTOM_FIELDS, {
    variables: {
      input: {
        filter: {
          workflowStep: SubStepTypes.RELATIONSHIP_INFORMATION,
          organizationId: activeOrganization?.id ?? undefined,
          keys: !isEmpty(enabledCustomFields) ? enabledCustomFields : undefined,
        },
        pagination: {
          perPage: 100,
        },
      },
    },
    skip: !customFieldsPermitted,
    onCompleted: async (res: any) => {
      setDerivedCustomFields(res?.fetchCustomFields?.customFields);
      const customFieldKeys = res?.fetchCustomFields?.customFields?.map((a: any) => (a.key));
      setDerivedCustomFieldsKeys(customFieldKeys);
    },
  });

  // loading 2 - user's data
  const { loading } = useQuery(
    FETCH_USER(permissions, derivedCustomFieldsKeys),
    {
      variables: {
        userId,
      },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      skip: customFieldsLoading,
      onCompleted: ((data) => setUserData({
        maritalStatus: data.fetchUser.user.maritalStatus || undefined,
        spouseFirstName: data.fetchUser.user.spouseFirstName || undefined,
        spouseMiddleName: data.fetchUser.user.spouseMiddleName || undefined,
        spouseLastName: data.fetchUser.user.spouseLastName || undefined,
        spouseEmail: data.fetchUser.user.spouseEmail || undefined,
        spouseSin: data.fetchUser.user.spouseSin || undefined,
        spouseTaxId: data.fetchUser.user.spouseTaxId || undefined,
        spouseTaxIdDisclosed: data.fetchUser.user.spouseTaxIdDisclosed || undefined,
        spouseTaxIdType: data.fetchUser.user.spouseTaxIdType || undefined,
        spouseEmploymentStatus: data.fetchUser.user.spouseEmploymentStatus || undefined,
        spouseEmployerName: data.fetchUser.user.spouseEmployerName || undefined,
        spouseEmployerCompanyAddress: data.fetchUser.user.spouseEmployerCompanyAddress ? {
          city: data.fetchUser.user.spouseEmployerCompanyAddress?.city || undefined,
          jurisdiction: data.fetchUser.user.spouseEmployerCompanyAddress?.jurisdiction || undefined,
          streetName: data.fetchUser.user.spouseEmployerCompanyAddress?.streetName || undefined,
          postal: data.fetchUser.user.spouseEmployerCompanyAddress?.postal || undefined,
          unitNumber: data.fetchUser.user.spouseEmployerCompanyAddress?.unitNumber || undefined,
          houseNumber: data.fetchUser.user.spouseEmployerCompanyAddress?.houseNumber || undefined,
          neighborhood: data.fetchUser.user.spouseEmployerCompanyAddress?.neighborhood || undefined,
          country: data.fetchUser.user.spouseEmployerCompanyAddress?.country || undefined,
        } : {} as PhysicalAddress,
        spouseEmployerCompanyPhone: data.fetchUser.user.spouseEmployerCompanyPhone || undefined,
        spouseCompanyType: data.fetchUser.user.spouseCompanyType || undefined,
        spouseJobTitle: data.fetchUser.user.spouseJobTitle || undefined,
        spouseAreaOfStudy: data.fetchUser.user.spouseAreaOfStudy || undefined,
        spouseDateOfBirth: data.fetchUser.user.spouseDateOfBirth || null,
        spouseAnnualIncomeCents: data.fetchUser.user.spouseAnnualIncomeCents || undefined,
        spouseAnnualIncomeStr: data.fetchUser.user.spouseAnnualIncomeStr || undefined,
        numberOfDependents: data.fetchUser.user.numberOfDependents || 0,
        trustedContactPerson: {
          name: data.fetchUser.user.trustedContactPerson?.name || undefined,
          physicalAddress: data.fetchUser.user.trustedContactPerson?.physicalAddress ? {
            city: data.fetchUser.user.trustedContactPerson?.physicalAddress?.city || undefined,
            jurisdiction: data.fetchUser.user.trustedContactPerson?.physicalAddress?.jurisdiction || undefined,
            streetName: data.fetchUser.user.trustedContactPerson?.physicalAddress?.streetName || undefined,
            postal: data.fetchUser.user.trustedContactPerson?.physicalAddress?.postal || undefined,
            unitNumber: data.fetchUser.user.trustedContactPerson?.physicalAddress?.unitNumber || undefined,
            houseNumber: data.fetchUser.user.trustedContactPerson?.physicalAddress?.houseNumber || undefined,
            neighborhood: data.fetchUser.user.trustedContactPerson?.physicalAddress?.neighborhood || undefined,
            country: data.fetchUser.user.trustedContactPerson?.physicalAddress?.country || undefined,
          } : {} as PhysicalAddress,
          email: data.fetchUser.user.trustedContactPerson?.email || undefined,
          phone: formatPhoneNumber(data.fetchUser.user.trustedContactPerson?.phone || undefined),
          relation: data.fetchUser.user.trustedContactPerson?.relation || undefined,
        },
        type: data.fetchUser.user.type || undefined,
        countryOfTaxResidence: data.fetchUser.user.countryOfTaxResidence || undefined,
        physicalAddress: data.fetchUser.user.physicalAddress,
        customFields: data.fetchUser.user.customFields || [],
      })),
    },
  );

  const [updateUser, { loading: updating }] = useMutation(UPDATE_USER, {
    onCompleted: () => {
      onNext?.();
    },
    variables: {
      input: {
        ...omit(['type', 'countryOfTaxResidence', 'physicalAddress', 'spouseTaxIdDisclosed'], userData),
        trustedContactPerson: {
          ...userData.trustedContactPerson,
          physicalAddress: isEmpty(userData?.trustedContactPerson?.physicalAddress) ? undefined : userData?.trustedContactPerson?.physicalAddress,
        },
        spouseEmployerCompanyAddress: isEmpty(userData.spouseEmployerCompanyAddress) ? undefined : userData.spouseEmployerCompanyAddress,
        ...(enabledCustomFields && derivedCustomFields && userData?.customFields ? {
          customFields: modifyUpdatingCustomFields(userData.customFields, derivedCustomFields, derivedCustomFieldsKeys),
        } : {}),
        userId,
      },
    },
    refetchQueries: [FETCH_USER(permissions)],
  });

  return (
    <RelationshipInformationVisual
      options={options}
      userData={userData}
      updateUser={setUserData}
      updating={updating}
      continueFunc={updateUser}
      loading={loading || stepLoading || customFieldsLoading}
      grid={grid}
      updateMode={updateMode}
      workflowCompletion={workflowCompletion}
      activeCustomFields={derivedCustomFieldsKeys}
    />
  );
};

export default RelationshipInformation;
